import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type TenantStatus = 'active' | 'suspended' | 'trial' | 'cancelled';
type SubscriptionPlan = 'free' | 'starter' | 'professional' | 'enterprise';

interface TenantMember {
  user_id: string;
  is_owner: boolean;
  profile: {
    email: string;
    full_name: string | null;
    phone: string | null; // Telefone do usuário
  } | null;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  plan: SubscriptionPlan;
  max_agents: number;
  max_channels: number;
  created_at: string;
  members: TenantMember[];
}

interface PlanInfo {
  id: string;
  name: string;
  max_inboxes: number;
  max_agents: number;
  max_channels: number;
}

const statusColors: Record<TenantStatus, string> = {
  active: 'bg-green-500/10 text-green-600 border-green-500/20',
  suspended: 'bg-red-500/10 text-red-600 border-red-500/20',
  trial: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  cancelled: 'bg-muted text-muted-foreground border-border',
};

const AdminTenants = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [loading, setLoading] = useState(true);
  // Estados para dialogs removidos temporariamente (funcionalidades de edição ocultadas)
  // const [dialogOpen, setDialogOpen] = useState(false);
  // const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  // const [editDialogOpen, setEditDialogOpen] = useState(false);
  // const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  // Estado para novo tenant removido temporariamente
  // const [newTenant, setNewTenant] = useState({
  //   name: '',
  //   slug: '',
  //   plan: 'free' as SubscriptionPlan,
  // });
  const { toast } = useToast();

  useEffect(() => {
    fetchTenants();
    // fetchPlans(); // Removido temporariamente - não necessário sem criação de tenant
  }, []);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id, name, max_inboxes, max_agents, max_channels')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  // Função removida temporariamente - não necessária sem criação de tenant
  // const getSelectedPlanLimits = () => {
  //   return plans.find(p => p.id === newTenant.plan);
  // };

  const fetchTenants = async () => {
    try {
      const { data: tenantsData, error: tenantsError } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (tenantsError) throw tenantsError;

      // Buscar membros com profiles
      const { data: membersData, error: membersError } = await supabase
        .from('tenant_members')
        .select('tenant_id, user_id, is_owner');

      if (membersError) throw membersError;

      // Buscar profiles dos membros
      const userIds = [...new Set((membersData || []).map(m => m.user_id))];
      // Buscar profiles incluindo o campo phone para exibição na tabela
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, full_name, phone')
        .in('id', userIds.length > 0 ? userIds : ['00000000-0000-0000-0000-000000000000']);

      if (profilesError) throw profilesError;

      const profilesMap = new Map((profilesData || []).map(p => [p.id, p]));

      // Agrupar membros por tenant
      const membersByTenant: Record<string, TenantMember[]> = {};
      (membersData || []).forEach(m => {
        if (!membersByTenant[m.tenant_id]) {
          membersByTenant[m.tenant_id] = [];
        }
        membersByTenant[m.tenant_id].push({
          user_id: m.user_id,
          is_owner: m.is_owner || false,
          profile: profilesMap.get(m.user_id) || null,
        });
      });

      const tenantsWithMembers = (tenantsData || []).map(tenant => ({
        ...tenant,
        members: membersByTenant[tenant.id] || [],
      }));

      setTenants(tenantsWithMembers);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os clientes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Funções de criação e edição removidas temporariamente
  // const createTenant = async () => { ... };
  // const handleOpenLinkDialog = (tenant: Tenant) => { ... };
  // const handleOpenEditDialog = (tenant: Tenant) => { ... };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Clientes</h1>
          <p className="text-muted-foreground">Gerencie as organizações do sistema</p>
        </div>
        {/* Botão "Novo Cliente" ocultado temporariamente */}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Lista de Clientes
          </CardTitle>
          <CardDescription>
            {tenants.length} organizações cadastradas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : tenants.length === 0 ? (
            <p className="text-muted-foreground">Nenhum cliente cadastrado</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cadastro</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Usuários Vinculados</TableHead>
                  <TableHead>Agentes</TableHead>
                  <TableHead>Canais</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => {
                  // Obtém dados do owner do tenant para exibição (telefone e email)
                  const owner = tenant.members.find(m => m.is_owner);
                  const ownerPhone = owner?.profile?.phone;
                  const ownerEmail = owner?.profile?.email;
                  return (
                  <TableRow key={tenant.id}>
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(tenant.created_at).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{tenant.slug}</TableCell>
                    <TableCell className="text-muted-foreground">{ownerPhone || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{ownerEmail || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColors[tenant.status]}>
                        {tenant.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{tenant.plan}</Badge>
                    </TableCell>
                    <TableCell>
                      {tenant.members.length === 0 ? (
                        <span className="text-muted-foreground text-sm">Nenhum</span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {tenant.members.map((member) => (
                            <div key={member.user_id} className="flex items-center gap-1">
                              <span className="text-sm">
                                {member.profile?.full_name || member.profile?.email || 'Usuário'}
                              </span>
                              {member.is_owner && (
                                <Badge variant="outline" className="text-xs px-1 py-0">
                                  Owner
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{tenant.max_agents}</TableCell>
                    <TableCell>{tenant.max_channels}</TableCell>
                    {/* Coluna de Ações removida temporariamente */}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs de edição removidos temporariamente */}
    </div>
  );
};

export default AdminTenants;
